import {isNil, isNotNil} from 'ramda';

import type {Nullish} from 'shared/web';

import type {FinancialProductCalculation} from '../schemas/FinancialProductCalculationSchema';
import type {FinancialProductMoney} from '../schemas/FinancialProductMoneySchema';
import type {LeasingCosts} from '../schemas/LeasingCostsSchema';
import {getCurrencyScale} from './getCurrencyScale';
import {QuoteDecimal} from './QuoteDecimal';

type LeasingTotals = Pick<
  FinancialProductCalculation,
  'leasingTotalMonthlyPayment' | 'leasingTotalForTerm'
>;

const NO_TOTALS: LeasingTotals = {leasingTotalMonthlyPayment: null, leasingTotalForTerm: null};

/** An amount finer than the rate's scale would be rounded by `toFixed`, so it is unusable. */
const isAddend = (money: FinancialProductMoney, currency: string, scale: number) => {
  const moneyScale = getCurrencyScale(money.amount);

  return money.currency === currency && isNotNil(moneyScale) && moneyScale <= scale;
};

/**
 * The backend leasing totals: the published rate plus the monthly services as entered (no VAT
 * maths), and that total times the term plus the leasing down payment. Same-currency sums and an
 * integer product are exact, so no rounding mode applies. Both null when the costs are unknown,
 * an amount is in another currency than the rate, or an amount is not a decimal at most as fine
 * as the rate's scale.
 */
export function calculateLeasingTotals(
  monthlyPayment: FinancialProductMoney,
  leasingCosts: LeasingCosts | Nullish,
  termMonths: number,
  leasingDownPayment: FinancialProductMoney
): LeasingTotals {
  const currency = monthlyPayment.currency;
  const scale = getCurrencyScale(monthlyPayment.amount);

  if (
    isNil(leasingCosts) ||
    isNil(scale) ||
    !isAddend(leasingCosts.monthlyServicesTotal, currency, scale) ||
    !isAddend(leasingDownPayment, currency, scale)
  ) {
    return NO_TOTALS;
  }

  const totalMonthlyPayment = new QuoteDecimal(monthlyPayment.amount).plus(
    leasingCosts.monthlyServicesTotal.amount
  );
  const totalForTerm = totalMonthlyPayment.times(termMonths).plus(leasingDownPayment.amount);

  return {
    leasingTotalMonthlyPayment: {amount: totalMonthlyPayment.toFixed(scale), currency},
    leasingTotalForTerm: {amount: totalForTerm.toFixed(scale), currency},
  };
}
